import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { invalidateProjectListQueries } from '@/lib/cache-utils';

interface DuplicateProjectModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  projectNumber?: number;  // Add project_number for template navigation
  isTemplate?: boolean;
}

export function DuplicateProjectModal({
  open,
  onClose,
  projectId,
  projectName,
  projectNumber,
  isTemplate = false,
}: DuplicateProjectModalProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      return api.projects.duplicate(projectId);
    },
    onSuccess: (data) => {
      const newProject = data.project;
      const itemsCopied = data.items_copied || 0;
      const scopesCopied = data.scopes_copied || 0;

      toast.success(`Created "${newProject.name}" with ${itemsCopied} items and ${scopesCopied} scopes`);

      // Invalidate list queries only — don't overwrite detail cache
      invalidateProjectListQueries(queryClient);

      onClose();

      // Navigate to the new project using id (UUID)
      if (newProject.id) {
        navigate(`/projects/${newProject.id}`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to duplicate project');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // For templates: Navigate to form for user to customize before creating
    if (isTemplate) {
      // Phase 7.5.13: Always use projectId (UUID) for template lookup
      // Templates now have NULL project_number
      navigate(`/projects/new?fromTemplate=${projectId}`);
      onClose();
      return;
    }

    // For regular projects: Duplicate immediately
    duplicateMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isTemplate ? 'Create Project from Template' : 'Duplicate Project'}
          </DialogTitle>
          <DialogDescription>
            {isTemplate
              ? `This will create a new proposal based on the template "${projectName}". All billable items and project scopes will be copied.`
              : `This will create a copy of "${projectName}" as a new proposal. All billable items and project scopes will be copied.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={duplicateMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={duplicateMutation.isPending}>
              {duplicateMutation.isPending
                ? 'Creating...'
                : isTemplate
                ? 'Create Project'
                : 'Duplicate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
